// AdminRecommendedActivities.js

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Download, Upload, Plus, Edit2, Trash2, 
  Save, X, ChevronDown, ChevronRight, GripVertical,
  AlertCircle, CheckCircle
} from 'lucide-react';
import api from '../api';

const AdminRecommendedActivities = () => {
  const [bands, setBands] = useState([]);
  const [questionSets, setQuestionSets] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingActivity, setEditingActivity] = useState(null);
  const [newActivity, setNewActivity] = useState(null);
  const [expandedBands, setExpandedBands] = useState({});
  const [expandedSets, setExpandedSets] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [bandsRes, setsRes, activitiesRes] = await Promise.all([
        api.get('/children/global-bands/'),
        api.get('/children/global-question-sets/'),
        api.get('/children/recommended-activities/')
      ]);
      setBands(bandsRes.data);
      setQuestionSets(setsRes.data);
      setActivities(activitiesRes.data);
      
      const bandsWithActivities = {};
      const setsWithActivities = {};
      bandsRes.data.forEach(band => {
        const bandSets = setsRes.data.filter(s => s.band === band.id);
        bandSets.forEach(set => {
          if (activitiesRes.data.some(a => a.band_set === set.id)) {
            setsWithActivities[set.id] = true;
            bandsWithActivities[band.id] = true;
          }
        });
      });
      setExpandedBands(bandsWithActivities);
      setExpandedSets(setsWithActivities);
    } catch (error) {
      showNotification('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      const response = await api.get('/children/recommended-activities/export/');
      const exportData = {
        version: '1.0',
        exportDate: new Date().toISOString(),
        activities: response.data
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recommended-activities-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showNotification('Activities exported successfully');
    } catch (error) {
      showNotification('Error exporting activities', 'error');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data && data.activities) {
          await api.post('/children/recommended-activities/import/', data.activities);
          showNotification('Activities imported successfully. Refreshing data...');
          fetchData();
        } else {
          showNotification('Invalid JSON format. Expected an object with an "activities" key.', 'error');
        }
      } catch (error) {
        showNotification(`Error importing file: ${error.message}`, 'error');
      }
    };
    reader.readAsText(file);
    
    event.target.value = '';
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to delete all recommended activities? This action cannot be undone.')) return;

    try {
      await api.post('/children/recommended-activities/clear_all/');
      showNotification('All activities have been deleted. Refreshing data...');
      fetchData();
    } catch (error) {
      showNotification('Error clearing activities', 'error');
    }
  };

  const validateWeek = (week, bandId) => {
    if (!week) return true;
    const band = bands.find(b => b.id === bandId);
    if (!band) return true;
    
    // Calculate absolute weeks based on age
    // 1 month ≈ 4.33 weeks
    const weeksPerMonth = 4.33;
    const minWeeks = Math.floor(band.age_min_months * weeksPerMonth);
    const maxWeeks = Math.ceil(band.age_max_months * weeksPerMonth) + 2; // Add buffer
    
    const weekNum = parseInt(week);

    if (isNaN(weekNum)) {
      showNotification('Please enter a valid week number', 'error');
      return false;
    }

    // For first band starting at 0 months, allow week 1
    const effectiveMin = minWeeks < 1 ? 1 : minWeeks;

    if (weekNum < effectiveMin) {
      showNotification(`Week number is below the band's age range. (Min: Week ${effectiveMin} for ${band.age_min_months} months)`, 'error');
      return false;
    }
    
    if (weekNum > maxWeeks) {
      showNotification(`Week number exceeds the band's age range. (Max: ~Week ${maxWeeks} for ${band.age_max_months} months)`, 'error');
      return false;
    }

    return true;
  };

  const handleAddActivity = (bandSetId, bandId) => {
    setNewActivity({
      band: bandId,
      band_set: bandSetId,
      title: '',
      description: '',
      week: '',
      order: activities.filter(a => a.band_set === bandSetId).length,
      is_active: true,
    });
    setEditingActivity(null);
  };

  const handleSaveNewActivity = async () => {
    if (!newActivity.title.trim() || !newActivity.description.trim()) {
      showNotification('Please enter title and description', 'error');
      return;
    }

    if (newActivity.week && !validateWeek(newActivity.week, newActivity.band)) {
      return;
    }

    try {
      const response = await api.post('/children/recommended-activities/', newActivity);
      setActivities(prev => [...prev, response.data]);
      setNewActivity(null);
      showNotification('Activity added successfully');
    } catch (error) {
      showNotification('Error adding activity', 'error');
    }
  };

  const handleEditActivity = (activity) => {
    setEditingActivity({ ...activity });
    setNewActivity(null);
  };

  const handleUpdateActivity = async () => {
    if (!editingActivity.title.trim() || !editingActivity.description.trim()) {
      showNotification('Please enter title and description', 'error');
      return;
    }

    if (editingActivity.week && !validateWeek(editingActivity.week, editingActivity.band)) {
      return;
    }

    try {
      const response = await api.patch(`/children/recommended-activities/${editingActivity.id}/`, editingActivity);
      setActivities(prev => prev.map(a => a.id === editingActivity.id ? response.data : a));
      setEditingActivity(null);
      showNotification('Activity updated successfully');
    } catch (error) {
      showNotification('Error updating activity', 'error');
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;

    try {
      await api.delete(`/children/recommended-activities/${activityId}/`);
      setActivities(prev => prev.filter(a => a.id !== activityId));
      showNotification('Activity deleted successfully');
    } catch (error) {
      showNotification('Error deleting activity', 'error');
    }
  };

  const handleDragStart = (e, activity, setId) => {
    setDraggedItem({ activity, setId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetActivity, setId) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.setId !== setId) return;
    
    const draggedActivity = draggedItem.activity;
    if (draggedActivity.id === targetActivity.id) return;

    const setActivities = activities.filter(a => a.band_set === setId);
    const draggedIndex = setActivities.findIndex(a => a.id === draggedActivity.id);
    const targetIndex = setActivities.findIndex(a => a.id === targetActivity.id);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const reorderedActivities = [...setActivities];
    const [removed] = reorderedActivities.splice(draggedIndex, 1);
    reorderedActivities.splice(targetIndex, 0, removed);

    const updatedActivities = reorderedActivities.map((activity, index) => ({
      ...activity,
      order: index
    }));

    setActivities(prev => {
      const otherActivities = prev.filter(a => a.band_set !== setId);
      return [...otherActivities, ...updatedActivities].sort((a, b) => {
        if (a.band !== b.band) return a.band - b.band;
        if (a.band_set !== b.band_set) return a.band_set - b.band_set;
        return a.order - b.order;
      });
    });

    try {
      const updatePromises = updatedActivities.map(activity =>
        api.patch(`/children/recommended-activities/${activity.id}/`, { order: activity.order })
      );
      await Promise.all(updatePromises);
      showNotification('Activity order updated successfully');
    } catch (error) {
      showNotification('Error updating order', 'error');
      fetchData();
    }

    setDraggedItem(null);
  };

  const toggleBandExpansion = (bandId) => {
    setExpandedBands(prev => ({ ...prev, [bandId]: !prev[bandId] }));
  };

  const toggleSetExpansion = (setId) => {
    setExpandedSets(prev => ({ ...prev, [setId]: !prev[setId] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-600">Loading recommended activities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] bg-clip-text text-transparent">
                Recommended Activities Management
              </h1>
              <p className="text-gray-600 mt-2">Create and manage recommended activities for different age bands</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleExport}
                className="px-5 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                Export JSON
              </button>
              <button
                onClick={() => document.getElementById('import-file').click()}
                className="px-5 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Upload className="w-5 h-5" />
                Import JSON
              </button>
              <button
                onClick={handleClearAll}
                className="px-5 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Trash2 className="w-5 h-5" />
                Clear All
              </button>
              <input type="file" id="import-file" className="hidden" onChange={handleImport} accept=".json" />
            </div>
          </div>
        </div>

        {/* Bands List */}
        <div className="space-y-4">
          {bands.map(band => {
            const bandSets = questionSets.filter(s => s.band === band.id);
            const bandActivities = activities.filter(a => a.band === band.id);
            
            return (
              <div key={band.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Band Header */}
                <div
                  onClick={() => toggleBandExpansion(band.id)}
                  className="p-6 bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] text-white cursor-pointer hover:from-[#2a4b73] hover:to-[#1e3a5f] transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {expandedBands[band.id] ? (
                        <ChevronDown className="w-6 h-6" />
                      ) : (
                        <ChevronRight className="w-6 h-6" />
                      )}
                      <div>
                        <h2 className="text-2xl font-bold">{band.title}</h2>
                        <p className="text-blue-100 text-sm mt-1">
                          {band.age_min_months}-{band.age_max_months} months • {bandActivities.length} activities
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-block px-4 py-2 bg-white/20 rounded-lg text-sm font-semibold">
                        {bandSets.length} categories
                      </span>
                    </div>
                  </div>
                </div>

                {/* Band Content */}
                {expandedBands[band.id] && (
                  <div className="p-6 bg-gray-50">
                    {bandSets.length > 0 ? bandSets.map(set => {
                      const setActivities = activities
                        .filter(a => a.band_set === set.id)
                        .sort((a, b) => a.order - b.order);

                      return (
                        <div key={set.id} className="mb-6 last:mb-0">
                          {/* Question Set Header */}
                          <div className="bg-white rounded-xl shadow-md overflow-hidden">
                            <div
                              onClick={() => toggleSetExpansion(set.id)}
                              className="p-5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white cursor-pointer hover:from-blue-600 hover:to-indigo-600 transition-all"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  {expandedSets[set.id] ? (
                                    <ChevronDown className="w-5 h-5" />
                                  ) : (
                                    <ChevronRight className="w-5 h-5" />
                                  )}
                                  <h3 className="text-xl font-bold">{set.title}</h3>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="px-3 py-1 bg-white/20 rounded-lg text-sm font-semibold">
                                    {setActivities.length} activities
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddActivity(set.id, band.id);
                                    }}
                                    className="px-4 py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2"
                                  >
                                    <Plus className="w-4 h-4" />
                                    Add Activity
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Activities List */}
                            {expandedSets[set.id] && (
                              <div className="p-5 bg-gray-50">
                                {/* New Activity Form */}
                                {newActivity?.band_set === set.id && (
                                  <div className="mb-4 p-5 bg-white rounded-xl border-2 border-dashed border-blue-400 shadow-sm">
                                    <div className="space-y-3">
                                      <input
                                        type="text"
                                        placeholder="Activity Title"
                                        value={newActivity.title}
                                        onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        autoFocus
                                      />
                                      <input
                                        type="number"
                                        placeholder="Week Number"
                                        value={newActivity.week}
                                        onChange={(e) => setNewActivity({ ...newActivity, week: e.target.value })}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                      <textarea
                                        placeholder="Activity Description"
                                        value={newActivity.description}
                                        onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                                      />
                                      <div className="flex items-center gap-3">
                                        <label className="flex items-center gap-2">
                                          <input
                                            type="checkbox"
                                            checked={newActivity.is_active}
                                            onChange={(e) => setNewActivity({ ...newActivity, is_active: e.target.checked })}
                                            className="w-4 h-4"
                                          />
                                          <span className="text-sm text-gray-600">Active</span>
                                        </label>
                                      </div>
                                      <div className="flex gap-2">
                                        <button
                                          onClick={handleSaveNewActivity}
                                          className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                        >
                                          <Save className="w-5 h-5" />
                                        </button>
                                        <button
                                          onClick={() => setNewActivity(null)}
                                          className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                        >
                                          <X className="w-5 h-5" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {setActivities.length > 0 ? (
                                  <div className="space-y-3">
                                    {setActivities.map((activity, index) => (
                                      <div
                                        key={activity.id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, activity, set.id)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, activity, set.id)}
                                        className={`p-4 bg-white rounded-xl border-2 border-gray-200 hover:shadow-md transition-all cursor-move ${
                                          !activity.is_active ? 'opacity-50' : ''
                                        }`}
                                      >
                                        {editingActivity?.id === activity.id ? (
                                          <div className="flex items-start gap-3">
                                            <GripVertical className="w-5 h-5 text-gray-400 mt-2" />
                                            <div className="flex-1">
                                              <input
                                                type="text"
                                                value={editingActivity.title}
                                                onChange={(e) => setEditingActivity({ ...editingActivity, title: e.target.value })}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                                              />
                                              <input
                                                type="number"
                                                placeholder="Week Number"
                                                value={editingActivity.week || ''}
                                                onChange={(e) => setEditingActivity({ ...editingActivity, week: e.target.value })}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                                              />
                                              <textarea
                                                value={editingActivity.description}
                                                onChange={(e) => setEditingActivity({ ...editingActivity, description: e.target.value })}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                                              />
                                              <div className="flex items-center gap-3 mt-3">
                                                <label className="flex items-center gap-2">
                                                  <input
                                                    type="checkbox"
                                                    checked={editingActivity.is_active}
                                                    onChange={(e) => setEditingActivity({ ...editingActivity, is_active: e.target.checked })}
                                                    className="w-4 h-4"
                                                  />
                                                  <span className="text-sm text-gray-600">Active</span>
                                                </label>
                                              </div>
                                            </div>
                                            <div className="flex gap-2">
                                              <button
                                                onClick={handleUpdateActivity}
                                                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                              >
                                                <Save className="w-5 h-5" />
                                              </button>
                                              <button
                                                onClick={() => setEditingActivity(null)}
                                                className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                                              >
                                                <X className="w-5 h-5" />
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="flex items-start gap-3">
                                            <GripVertical className="w-5 h-5 text-gray-400 mt-1" />
                                            <div className="flex-1">
                                              <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                  <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full mb-2">
                                                    Activity {index + 1}
                                                  </span>
                                                  <h4 className="text-lg font-bold text-gray-800 mb-2">{activity.title}</h4>
                                                  {activity.week && (
                                                    <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full mb-2 mr-2">
                                                      Week {activity.week}
                                                    </span>
                                                  )}
                                                  <p className="text-gray-600 text-sm leading-relaxed">{activity.description}</p>
                                                  {!activity.is_active && (
                                                    <span className="inline-block mt-2 px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded-full">
                                                      Inactive
                                                    </span>
                                                  )}
                                                </div>
                                                <div className="flex gap-2 ml-4">
                                                  <button
                                                    onClick={() => handleEditActivity(activity)}
                                                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                                  >
                                                    <Edit2 className="w-4 h-4" />
                                                  </button>
                                                  <button
                                                    onClick={() => handleDeleteActivity(activity.id)}
                                                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                                  >
                                                    <Trash2 className="w-4 h-4" />
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-8 text-gray-500">
                                    No activities added for this category yet.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }) : (
                      <div className="text-center py-8 text-gray-500">
                        No question sets for this age band yet.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg transition-all transform ${
          notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white z-50`}>
          <div className="flex items-center gap-2">
            {notification.type === 'error' ? (
              <AlertCircle className="w-5 h-5" />
            ) : (
              <CheckCircle className="w-5 h-5" />
            )}
            <span>{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRecommendedActivities;
